import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Loader2, Search, Eye, EyeOff,
  Image as ImageIcon, Video, FileText, Upload, X, Sparkles,
  Calendar, Clock, ExternalLink, CheckCircle, XCircle,
  Mail, Send, Users, MessageCircle, Bold, Italic, Underline,
  List, ListOrdered, Heading1, Heading2, Quote, Code, Link as LinkIcon
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import TiptapImage from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import TiptapUnderline from '@tiptap/extension-underline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOG_CATEGORIES = [
  'General',
  'Books & Reading',
  'Crystals & Healing',
  'Wellness & Mindfulness',
  'Store News',
  'Tips & Guides',
  'Behind the Scenes',
  'Community',
];

// Rich Text Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/50">
      <Button
        type="button"
        variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className="h-8 w-8 p-0"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className="h-8 w-8 p-0"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('underline') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className="h-8 w-8 p-0"
      >
        <Underline className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button
        type="button"
        variant={editor.isActive('heading', { level: 1 }) ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className="h-8 w-8 p-0"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('heading', { level: 2 }) ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className="h-8 w-8 p-0"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button
        type="button"
        variant={editor.isActive('bulletList') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className="h-8 w-8 p-0"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('orderedList') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className="h-8 w-8 p-0"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button
        type="button"
        variant={editor.isActive('blockquote') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className="h-8 w-8 p-0"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('codeBlock') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className="h-8 w-8 p-0"
      >
        <Code className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button
        type="button"
        variant={editor.isActive('link') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={addLink}
        className="h-8 w-8 p-0"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addImage}
        className="h-8 w-8 p-0"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Rich Text Editor Component
const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapUnderline,
      TiptapLink.configure({
        openOnClick: false,
      }),
      TiptapImage,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  return (
    <div className="border rounded-md overflow-hidden">
      <EditorToolbar editor={editor} />
      <EditorContent 
        editor={editor} 
        className="min-h-[250px] p-4 prose prose-sm max-w-none focus:outline-none"
      />
      <style>{`
        .ProseMirror { min-height: 250px; outline: none; }
        .ProseMirror p { margin-bottom: 0.5em; }
        .ProseMirror h1 { font-size: 1.5em; font-weight: bold; margin-top: 1em; }
        .ProseMirror h2 { font-size: 1.25em; font-weight: bold; margin-top: 0.75em; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5em; margin: 0.5em 0; }
        .ProseMirror blockquote { border-left: 3px solid hsl(var(--border)); padding-left: 1em; margin: 0.5em 0; color: hsl(var(--muted-foreground)); }
        .ProseMirror pre { background: hsl(var(--muted)); padding: 0.75em; border-radius: 0.25em; overflow-x: auto; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 0.25em; }
        .ProseMirror a { color: hsl(var(--primary)); text-decoration: underline; }
      `}</style>
    </div>
  );
};

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  // Newsletter state
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [newsletterPost, setNewsletterPost] = useState(null);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [subscriberStats, setSubscriberStats] = useState({ total: 0, active: 0 });
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    cover_image: '',
    category: 'General',
    tags: '',
    is_published: false,
    is_featured: false,
    video_url: '',
    document_url: '',
    document_name: '',
  });

  useEffect(() => {
    fetchPosts();
    fetchSubscriberStats();
  }, []);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriberStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriberStats({
        total: response.data.total,
        active: response.data.active
      });
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
    }
  };

  const openDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        cover_image: post.cover_image || '',
        category: post.category || 'General',
        tags: (post.tags || []).join(', '),
        is_published: post.is_published || false,
        is_featured: post.is_featured || false,
        video_url: post.video_url || '',
        document_url: post.document_url || '',
        document_name: post.document_name || '',
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        cover_image: '',
        category: 'General',
        tags: '',
        is_published: false,
        is_featured: false,
        video_url: '',
        document_url: '',
        document_name: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload JPG, PNG, GIF, or WEBP image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        },
      });

      setFormData((prev) => ({ ...prev, cover_image: response.data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      toast.error('Please upload PDF, DOC, DOCX, or TXT file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('Document must be less than 20MB');
      return;
    }

    setUploadingDoc(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/blog/upload-document`, uploadData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        },
      });

      setFormData((prev) => ({ 
        ...prev, 
        document_url: response.data.document_url,
        document_name: response.data.document_name
      }));
      toast.success('Document uploaded!');
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      };

      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/admin/blog`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created!');
      }

      setIsDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    setDeleting(postId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleting(null);
    }
  };

  const handleTogglePublish = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/blog/${postId}/toggle-publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(prev => prev.map(p => 
        p.id === postId ? { ...p, is_published: response.data.is_published } : p
      ));
      
      toast.success(response.data.is_published ? 'Post published!' : 'Post unpublished');
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  // Newsletter functions
  const openNewsletterDialog = (post) => {
    setNewsletterPost(post);
    setNewsletterSubject(post.title);
    setIsNewsletterOpen(true);
  };

  const handleSendNewsletter = async () => {
    if (!newsletterSubject.trim()) {
      toast.error('Subject is required');
      return;
    }

    setSendingNewsletter(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/newsletter/send`, {
        subject: newsletterSubject,
        post_id: newsletterPost?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setIsNewsletterOpen(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send newsletter';
      toast.error(msg);
    } finally {
      setSendingNewsletter(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'published' && post.is_published) ||
      (filterStatus === 'draft' && !post.is_published);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-blog">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground">Blog & Newsletter</h2>
          <p className="text-muted-foreground">
            {posts.length} posts • {subscriberStats.active} subscribers
          </p>
        </div>
        <Button onClick={() => openDialog()} className="btn-accent" data-testid="add-post-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Posts</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No posts yet</h3>
            <p className="text-muted-foreground mb-4">Create your first blog post to share with your customers.</p>
            <Button onClick={() => openDialog()} className="btn-accent">
              <Plus className="h-4 w-4 mr-2" />
              Create Post
            </Button>
          </Card>
        ) : (
          filteredPosts.map((post) => (
            <Card key={post.id} className="overflow-hidden" data-testid={`post-${post.id}`}>
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row">
                  {/* Thumbnail */}
                  <div className="w-full sm:w-48 h-32 sm:h-auto bg-muted flex-shrink-0">
                    {post.cover_image ? (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                        <FileText className="h-8 w-8 text-primary/30" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 sm:p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge variant={post.is_published ? 'default' : 'secondary'}>
                        {post.is_published ? (
                          <><CheckCircle className="h-3 w-3 mr-1" /> Published</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" /> Draft</>
                        )}
                      </Badge>
                      <Badge variant="outline">{post.category}</Badge>
                      {post.is_featured && (
                        <Badge className="bg-accent text-accent-foreground">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                      {post.video_url && (
                        <Badge variant="outline" className="text-red-600 border-red-200">
                          <Video className="h-3 w-3 mr-1" />
                          Video
                        </Badge>
                      )}
                      {post.document_url && (
                        <Badge variant="outline" className="text-blue-600 border-blue-200">
                          <FileText className="h-3 w-3 mr-1" />
                          Doc
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-serif text-lg font-semibold text-foreground mb-1 line-clamp-1">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {post.excerpt || post.content?.replace(/<[^>]*>/g, '').slice(0, 150)}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {post.views || 0} views
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" />
                          {post.comment_count || 0}
                        </span>
                      </div>

                      <div className="flex items-center gap-1">
                        {post.is_published && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openNewsletterDialog(post)}
                            title="Send as Newsletter"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTogglePublish(post.id)}
                          title={post.is_published ? 'Unpublish' : 'Publish'}
                        >
                          {post.is_published ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        {post.is_published && (
                          <a
                            href={`/blog/${post.slug || post.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm" title="View">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog(post)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          disabled={deleting === post.id}
                          className="text-destructive hover:text-destructive"
                        >
                          {deleting === post.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter post title..."
                required
              />
            </div>

            {/* Excerpt */}
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                placeholder="Brief summary of the post..."
                rows={2}
              />
            </div>

            {/* Rich Text Editor */}
            <div className="space-y-2">
              <Label>Content *</Label>
              <RichTextEditor
                content={formData.content}
                onChange={handleContentChange}
              />
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <Label>Cover Image</Label>
              <div className="flex gap-4 items-start">
                {formData.cover_image ? (
                  <div className="relative w-32 h-20 rounded-lg overflow-hidden bg-muted">
                    <img
                      src={formData.cover_image}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, cover_image: '' }))}
                      className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-20 rounded-lg border-2 border-dashed flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input
                    name="cover_image"
                    value={formData.cover_image}
                    onChange={handleInputChange}
                    placeholder="Image URL or upload..."
                  />
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> Upload Image</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Category and Tags */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOG_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="books, reading, tips"
                />
              </div>
            </div>

            {/* Video URL */}
            <div className="space-y-2">
              <Label htmlFor="video_url">
                <Video className="h-4 w-4 inline mr-2" />
                Video URL (YouTube, Vimeo, or direct link)
              </Label>
              <Input
                id="video_url"
                name="video_url"
                value={formData.video_url}
                onChange={handleInputChange}
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            {/* Document Upload */}
            <div className="space-y-2">
              <Label>
                <FileText className="h-4 w-4 inline mr-2" />
                Attached Document (PDF, DOC, TXT)
              </Label>
              {formData.document_url ? (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="flex-1 text-sm">{formData.document_name || 'Document'}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      document_url: '', 
                      document_name: '' 
                    }))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => docInputRef.current?.click()}
                    disabled={uploadingDoc}
                  >
                    {uploadingDoc ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> Upload Document</>
                    )}
                  </Button>
                </>
              )}
            </div>

            {/* Toggles */}
            <div className="flex flex-wrap gap-6 pt-4 border-t">
              <div className="flex items-center gap-3">
                <Switch
                  id="is_published"
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                />
                <Label htmlFor="is_published" className="font-normal">
                  Publish immediately
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                />
                <Label htmlFor="is_featured" className="font-normal">
                  <Sparkles className="h-4 w-4 inline mr-1 text-accent" />
                  Featured post
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="btn-accent">
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  editingPost ? 'Update Post' : 'Create Post'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Newsletter Dialog */}
      <Dialog open={isNewsletterOpen} onOpenChange={setIsNewsletterOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send as Newsletter
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Will be sent to <strong>{subscriberStats.active}</strong> active subscribers
                </span>
              </div>
              {newsletterPost && (
                <p className="text-sm font-medium line-clamp-2">{newsletterPost.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newsletter-subject">Email Subject</Label>
              <Input
                id="newsletter-subject"
                value={newsletterSubject}
                onChange={(e) => setNewsletterSubject(e.target.value)}
                placeholder="Enter email subject..."
              />
            </div>

            <p className="text-xs text-muted-foreground">
              The newsletter will include the post's cover image, excerpt, and a link to read the full article.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewsletterOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendNewsletter} 
              disabled={sendingNewsletter || !newsletterSubject.trim()}
              className="btn-accent"
            >
              {sendingNewsletter ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send Newsletter</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlog;
